<template>
  <div id="view-athletics">
    <LoadingIndicator v-if="loading" />
    <cms-athletics
      v-else-if="!!content.length"
      :options="themeSettings"
      :content="content"
      :athletics-config="config"
      :component-id="componentId"
      :path-prefix="pathPrefix"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useMainStore } from '../../../stores/mainStore.js';
import { useTranslationStore } from '../../../stores/translationStore.js';
import LoadingIndicator from '../../global/LoadingIndicator';
import HomepageFetchHelper from '../../../helpers/homepage-fetcher.js';

defineOptions({
  name: 'ViewAthletics',
});

const props = defineProps({
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
  containerId: {
    type: String,
    default: null,
  },
});

const mainStore = useMainStore();
const translationStore = useTranslationStore();

const nuxtGlobs = { axios: null, staticFetch: null };

const loading = ref(true);
const content = ref([]);
const config = ref({});

const homepage = computed(() => mainStore.homepage);
const theme = computed(() => mainStore.theme);
const isThemePreview = computed(() => mainStore.isThemePreview);
const featureFlags = computed(() => mainStore.featureFlags);
const staticFilesConfig = computed(() => mainStore.staticFilesConfig);
const mediaPreviewContent = computed(() => mainStore.mediaPreviewContent);

const defaultData = computed(() => mediaPreviewContent.value?.athleticsPreviewContent || []);

const themeSettings = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...(theme.value.containers[props.containerIdx]?.components?.[props.componentIdx]?.options || {}),
    translations: translationStore.getComponentTranslation('athletics'),
    globalThemeSettings: mainStore.getGlobalThemeSettings,
  };
});

const componentId = computed(() => {
  if (isThemePreview.value) {
    return 'preview_id';
  }
  return `${props.containerId}_${props.componentIdx}`;
});

const pathPrefix = computed(() => mainStore.pathPrefix);
const useS3ForMedia = computed(() => featureFlags.value.cms_stored_data_athletics_enabled);

async function fetchContentFromS3AndValidate() {
  try {
    const s3UrlForMedia = mainStore.getS3UrlForMedia('athletics');
    const contentResponse = await nuxtGlobs.staticFetch.get(s3UrlForMedia);

    const lastS3FileUpdate = contentResponse.data?.meta?.last_static_update;
    const lastMediaUpdate =
      staticFilesConfig.value?.last_static_update?.athletics ||
      staticFilesConfig.value?.homepage?.last_static_update?.athletics;
    const isStaticDataFresh = HomepageFetchHelper.validateLastStaticUpdate(lastS3FileUpdate, lastMediaUpdate);
    const athleticsEvents = contentResponse?.data?.scores_schedules;

    if (
      !isStaticDataFresh ||
      !athleticsEvents ||
      HomepageFetchHelper.hasOutdatedEntries(athleticsEvents, 'athletics')
    ) {
      return await fetchContentFromThrillshare();
    }

    return athleticsEvents;
  } catch (e) {
    console.error(e);
    return await fetchContentFromThrillshare();
  }
}

async function fetchContentFromThrillshare() {
  try {
    const { url, params } = mainStore.getAthletics(nuxtGlobs.axios.defaults.baseURL);
    const contentResponse = await nuxtGlobs.axios.get(url, { params, cache: { ignoreCache: false } });
    return contentResponse?.data?.scores_schedules || [];
  } catch (e) {
    console.error(e);
    return [];
  }
}

function setupThemePreview() {
  content.value = defaultData.value;
  loading.value = false;
}

async function getContent() {
  if (isThemePreview.value) {
    setupThemePreview();
    return;
  }

  if (useS3ForMedia.value) {
    content.value = await fetchContentFromS3AndValidate();
  } else {
    content.value = await fetchContentFromThrillshare();
  }
  loading.value = false;
}

onMounted(() => {
  const inst = getCurrentInstance();
  const p = inst?.proxy;
  const gp = inst?.appContext.config.globalProperties;
  nuxtGlobs.axios = p?.$axios ?? gp?.$axios;
  nuxtGlobs.staticFetch = p?.$staticFetch ?? gp?.$staticFetch;
  config.value = homepage.value.athletics || {};
  getContent();
});
</script>

<style scoped lang="scss">
#view-athletics {
  width: 100%;
  height: 100%;
}
</style>
